import permissionRegistry from '../constants/permissionRegistry.json';

export const ALLOW = 'allow';
export const DENY = 'deny';

function getRegistry(registry = permissionRegistry) {
  return registry || permissionRegistry;
}

export function getPermissionRegistry() {
  return permissionRegistry;
}

export function getRoleDefinitions(registry) {
  return getRegistry(registry).roleDefinitions || {};
}

export function getAllPermissionKeys(registry) {
  return getRegistry(registry).modules.flatMap((module) =>
    module.actions.map((action) => `${module.key}.${action}`)
  );
}

export function getPermissionRule(permissionKey, registry) {
  return getRegistry(registry).permissionRules?.[permissionKey] || {};
}

export function getRoleDefaultPermissions(role, registry) {
  const currentRegistry = getRegistry(registry);
  const defaults = currentRegistry.roleDefaults?.[role] || {};
  if (defaults['*']) {
    return new Set(getAllPermissionKeys(currentRegistry));
  }

  const granted = new Set();
  currentRegistry.modules.forEach((module) => {
    const actions = defaults[module.key] || [];
    if (actions.includes('*')) {
      module.actions.forEach((action) => granted.add(`${module.key}.${action}`));
      return;
    }
    actions.forEach((action) => {
      if (module.actions.includes(action)) {
        granted.add(`${module.key}.${action}`);
      }
    });
  });
  return granted;
}

export function normalizePermissionOverrides(overrides, registry) {
  if (!overrides || typeof overrides !== 'object') return {};
  const knownKeys = new Set(getAllPermissionKeys(registry));
  return Object.entries(overrides).reduce((acc, [key, value]) => {
    if (knownKeys.has(key) && (value === ALLOW || value === DENY)) {
      acc[key] = value;
    }
    return acc;
  }, {});
}

export function getEffectivePermissions(user, registry) {
  if (!user) return new Set();
  // Always recompute from role + overrides against the CURRENT (bundled) registry rather
  // than trusting user.effective_permissions — that array is a snapshot taken once at
  // login time. Any module added to the registry after a user's last login (e.g. the
  // `tracking` module) would never appear for them until they logged out and back in,
  // since the stale cached array was preferred over recomputing. The two computations are
  // provably identical (this mirrors backend/utils/permissions.py's
  // effective_permissions_for_user() exactly), so always recomputing loses nothing and
  // fixes the staleness.
  const granted = getRoleDefaultPermissions(user.role, registry);
  const overrides = normalizePermissionOverrides(user.permission_overrides, registry);
  Object.entries(overrides).forEach(([key, value]) => {
    if (value === ALLOW) granted.add(key);
    if (value === DENY) granted.delete(key);
  });
  return granted;
}

export function hasPermission(user, permissionKey, registry) {
  return getEffectivePermissions(user, registry).has(permissionKey);
}

export function canAccessModule(user, moduleKey, registry) {
  return hasPermission(user, `${moduleKey}.view`, registry);
}

export function getModuleByRoute(routePath = '', registry) {
  const currentRegistry = getRegistry(registry);
  const normalized = routePath.replace(/^\/+/, '');
  const candidates = [];

  currentRegistry.modules.forEach((module) => {
    (module.routes || []).forEach((pattern) => {
      if (!pattern) {
        if (normalized === '' || normalized === 'dashboard') {
          candidates.push({ module, score: 0 });
        }
        return;
      }
      const regex = new RegExp(`^${pattern.replace(/:[^/]+/g, '[^/]+')}$`);
      if (regex.test(normalized)) {
        candidates.push({ module, score: pattern.length });
      }
    });
  });

  candidates.sort((a, b) => b.score - a.score);
  return candidates[0]?.module || null;
}

export function formatPermissionLabel(permissionKey, registry) {
  const rule = getPermissionRule(permissionKey, registry);
  if (rule.label) return rule.label;

  const [, action = permissionKey] = permissionKey.split('.');
  return action
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export function getRequiredPermissionKeys(permissionKey, registry) {
  const required = new Set(getPermissionRule(permissionKey, registry).requires || []);
  const [moduleKey, action] = permissionKey.split('.');
  if (moduleKey && action && action !== 'view') {
    required.add(`${moduleKey}.view`);
  }
  return Array.from(required);
}

export function validatePermissionDependencies(role, overrides, registry) {
  const currentRegistry = getRegistry(registry);
  const effective = getEffectivePermissions(
    { role, permission_overrides: normalizePermissionOverrides(overrides, currentRegistry) },
    currentRegistry
  );

  const issues = [];
  effective.forEach((permissionKey) => {
    const missing = getRequiredPermissionKeys(permissionKey, currentRegistry).filter(
      (requiredKey) => !effective.has(requiredKey)
    );
    if (missing.length > 0) {
      issues.push({
        permissionKey,
        missing,
      });
    }
  });

  return issues;
}

export function getHighRiskPermissionKeys(role, overrides, registry) {
  const currentRegistry = getRegistry(registry);
  const effective = getEffectivePermissions(
    { role, permission_overrides: normalizePermissionOverrides(overrides, currentRegistry) },
    currentRegistry
  );

  return Array.from(effective).filter((permissionKey) => {
    const risk = getPermissionRule(permissionKey, currentRegistry).risk;
    return risk === 'high' || risk === 'critical';
  });
}

export function getPermissionGroups(registry) {
  return getRegistry(registry).groups || [];
}
